import React, { useState, useEffect } from "react";
import { Grid, Switch, FormControlLabel, TextField, Card } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import { toast } from "react-toastify";
import { TailSpin } from "react-loader-spinner";
import { useSelector, useDispatch } from "react-redux";
import { userSelector } from "store/features/authSlice";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { firestore } from "firebaseConfig";

const AutomatedMessageSettings = () => {
  const { uid, name: adminName } = useSelector(userSelector);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Settings state
  const [automatedWishesEnabled, setAutomatedWishesEnabled] = useState(true);
  const [defaultBirthdayMessage, setDefaultBirthdayMessage] = useState("");
  const [defaultAnniversaryMessage, setDefaultAnniversaryMessage] =
    useState("");

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, [uid]);

  const loadSettings = async () => {
    if (!uid) return;

    setLoading(true);
    try {
      const profileRef = doc(firestore, "profile", uid);
      const profileSnap = await getDoc(profileRef);

      if (profileSnap.exists()) {
        const data = profileSnap.data();
        setAutomatedWishesEnabled(data.automatedWishesEnabled ?? true);
        setDefaultBirthdayMessage(data.defaultBirthdayMessage ?? "");
        setDefaultAnniversaryMessage(data.defaultAnniversaryMessage ?? "");
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!uid) return;

    setSaving(true);
    try {
      const profileRef = doc(firestore, "profile", uid);
      await updateDoc(profileRef, {
        automatedWishesEnabled,
        defaultBirthdayMessage,
        defaultAnniversaryMessage,
        updated_at: new Date().toISOString(),
      });

      toast.success("Settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card sx={{ height: "100%", width: "100%", overflow: "initial" }}>
        <SuiBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
          p={3}
        >
          <TailSpin color="#17c1e8" height={40} width={40} />
        </SuiBox>
      </Card>
    );
  }

  return (
    <Card sx={{ height: "100%", width: "100%", overflow: "initial" }}>
      <SuiBox
        display="flex"
        flexDirection="column"
        padding={3}
        gap={1}
        width="100%"
        height="100%"
        justifyContent="space-between"
      >
        <SuiBox mb={2}>
          <SuiTypography variant="h6" fontWeight="medium">
            Automated Birthday & Anniversary Messages
          </SuiTypography>
          <SuiTypography variant="body2" color="text" mt={1}>
            Configure automated messages for client birthdays and anniversaries
          </SuiTypography>
        </SuiBox>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SuiBox ml={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={automatedWishesEnabled}
                    onChange={(e) =>
                      setAutomatedWishesEnabled(e.target.checked)
                    }
                    color="primary"
                  />
                }
                label={
                  <SuiBox>
                    <SuiTypography variant="body2" fontWeight="medium">
                      Enable Automated Messages
                    </SuiTypography>
                    <SuiTypography variant="caption" color="text">
                      Automatically send birthday and anniversary wishes daily
                      at 9:00 AM IST
                    </SuiTypography>
                  </SuiBox>
                }
              />
            </SuiBox>
          </Grid>

          {automatedWishesEnabled && (
            <>
              <Grid item xs={12}>
                <SuiBox>
                  <SuiTypography variant="body2" fontWeight="medium" mb={1}>
                    Default Birthday Message
                  </SuiTypography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={defaultBirthdayMessage}
                    onChange={(e) => setDefaultBirthdayMessage(e.target.value)}
                    placeholder="Happy Birthday! 🎂 May this special day bring you joy, good health, and wonderful moments. We're grateful to have you as part of our wellness family!"
                    variant="outlined"
                    sx={{
                      width: "100% !important",
                      "& .MuiInputBase-input": {
                        width: "100% !important",
                        maxWidth: "100% !important",
                        minWidth: "100% !important",
                      },
                    }}
                  />
                  <SuiTypography variant="caption" color="text" mt={1}>
                    Leave empty to use system default message
                  </SuiTypography>
                </SuiBox>
              </Grid>

              <Grid item xs={12}>
                <SuiBox>
                  <SuiTypography variant="body2" fontWeight="medium" mb={1}>
                    Default Anniversary Message
                  </SuiTypography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={defaultAnniversaryMessage}
                    onChange={(e) =>
                      setDefaultAnniversaryMessage(e.target.value)
                    }
                    placeholder="Happy Anniversary! 🎊 Congratulations on this special milestone. Thank you for trusting us with your health journey. Here's to many more years of wellness together!"
                    variant="outlined"
                    size="small"
                    sx={{
                      width: "100% !important",
                      "& .MuiInputBase-input": {
                        width: "100% !important",
                        maxWidth: "100% !important",
                        minWidth: "100% !important",
                      },
                    }}
                  />
                  <SuiTypography variant="caption" color="text" mt={1}>
                    Leave empty to use system default message
                  </SuiTypography>
                </SuiBox>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <SuiBox display="flex" justifyContent="flex-end">
              <SuiButton
                color="dark"
                variant="gradient"
                onClick={saveSettings}
                disabled={saving}
                sx={{ minWidth: "120px" }}
              >
                {saving ? (
                  <TailSpin color="#fff" height={20} width={20} />
                ) : (
                  "Save Settings"
                )}
              </SuiButton>
            </SuiBox>
          </Grid>
        </Grid>

        <SuiBox mt={3} p={2} backgroundColor="grey.100" borderRadius="md">
          <SuiTypography variant="body2" color="text">
            <strong>Note:</strong> When automated messages are enabled, clients
            will automatically receive birthday and anniversary wishes with
            their email included in CC. This allows them to see that the message
            was sent to them on their special day.
          </SuiTypography>
        </SuiBox>
      </SuiBox>
    </Card>
  );
};

export default AutomatedMessageSettings;
